const dayjs = require("dayjs");
const duration = require("dayjs/plugin/duration");
const relativeTime = require("dayjs/plugin/relativeTime");
const customParseFormat = require("dayjs/plugin/customParseFormat");

require("dayjs/locale/mn");

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.locale("mn");

// ═══════════════════════════════════════════════════
// 📅 ОГНОО / ЦАГ
// ═══════════════════════════════════════════════════

/**
 * 2024-01-15  →  "2024.01.15"
 */
const formatDate = (date) => {
  if (!date) return "-";
  return dayjs(date).format("YYYY.MM.DD");
};

/**
 * 2024-01-15T10:30:00  →  "2024.01.15 10:30"
 */
const formatDateTime = (date) => {
  if (!date) return "-";
  return dayjs(date).format("YYYY.MM.DD HH:mm");
};

/**
 * 2024-01-15T10:30:00  →  "10:30"
 */
const formatTime = (date) => {
  if (!date) return "-";
  return dayjs(date).format("HH:mm");
};

/**
 * 2024-01-15  →  "2024 оны 01 сарын 15"
 */
const formatDateFull = (date) => {
  if (!date) return "-";
  return dayjs(date).format("YYYY оны MM сарын DD");
};

/**
 * Харьцангуй цаг — "3 цагийн өмнө", "2 өдрийн өмнө"
 */
const formatRelative = (date) => {
  if (!date) return "-";
  return dayjs(date).fromNow();
};

/**
 * Хоёр огноо хоорондын зөрүү
 * unit: "day" | "hour" | "minute" | "month" | "year"
 */
const dateDiff = (start, end, unit = "day") => {
  if (!start || !end) return 0;
  return dayjs(end).diff(dayjs(start), unit);
};

/**
 * Өнөөдөр мөн эсэх
 */
const isToday = (date) => {
  if (!date) return false;
  return dayjs(date).isSame(dayjs(), "day");
};

/**
 * Огноо өнгөрсөн эсэх
 */
const isPast = (date) => {
  if (!date) return false;
  return dayjs(date).isBefore(dayjs());
};

/**
 * N хоног/сар/жил нэмэх
 * unit: "day" | "month" | "year" | "hour" | "minute"
 */
const addTime = (date, amount, unit = "day") => {
  if (!date) return null;
  return dayjs(date).add(amount, unit).toDate();
};

// ═══════════════════════════════════════════════════
// ⏱ ХУГАЦААНЫ УРТЫГ ХАРУУЛАХ
// ═══════════════════════════════════════════════════

/**
 * Секундийг уншигдахуйц болгох
 * 3600  →  "1 цаг"
 * 5400  →  "1 цаг 30 минут"
 * 90    →  "1 минут 30 секунд"
 */
const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "-";

  const d = dayjs.duration(seconds, "seconds");
  const years = Math.floor(d.asYears());
  const months = Math.floor(d.asMonths());
  const days = Math.floor(d.asDays());
  const hours = Math.floor(d.asHours());
  const mins = d.minutes();
  const secs = d.seconds();

  if (years > 0)
    return `${years} жил${months % 12 > 0 ? ` ${months % 12} сар` : ""}`;
  if (months > 0)
    return `${months} сар${days % 30 > 0 ? ` ${days % 30} өдөр` : ""}`;
  if (days > 0)
    return `${days} өдөр${hours % 24 > 0 ? ` ${hours % 24} цаг` : ""}`;
  if (hours > 0) return `${hours} цаг${mins > 0 ? ` ${mins} минут` : ""}`;
  if (mins > 0) return `${mins} минут${secs > 0 ? ` ${secs} секунд` : ""}`;
  return `${secs} секунд`;
};

/**
 * Сар тоогоор харуулах
 * 18  →  "1 жил 6 сар"
 * 6   →  "6 сар"
 */
const formatMonths = (months) => {
  if (!months || months < 0) return "-";
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y > 0 && m > 0) return `${y} жил ${m} сар`;
  if (y > 0) return `${y} жил`;
  return `${m} сар`;
};

/**
 * Хоёр огноо хоорондын хугацааг харуулах
 * ("2024-01-01", "2025-07-01")  →  "1 жил 6 сар"
 */
const formatDateRange = (start, end) => {
  if (!start || !end) return "-";
  const totalMonths = dayjs(end).diff(dayjs(start), "month");
  return formatMonths(totalMonths);
};

// ═══════════════════════════════════════════════════
// 💰 МӨНГӨН ДҮН
// ═══════════════════════════════════════════════════

const CURRENCY_CONFIG = {
  MNT: { locale: "mn-MN", currency: "MNT", symbol: "₮" },
  USD: { locale: "en-US", currency: "USD", symbol: "$" },
  EUR: { locale: "de-DE", currency: "EUR", symbol: "€" },
  CNY: { locale: "zh-CN", currency: "CNY", symbol: "¥" },
};

/**
 * Мөнгөн дүн форматлах
 * formatMoney(1500000)           →  "1,500,000 ₮"
 * formatMoney(1500000, "USD")    →  "$1,500,000"
 * formatMoney(1500000, "MNT", { compact: true })  →  "1.5M ₮"
 */
const formatMoney = (amount, currency = "MNT", options = {}) => {
  if (amount === null || amount === undefined) return "-";

  const { compact = false, decimals } = options;
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.MNT;

  if (compact) {
    return formatMoneyCompact(amount, currency);
  }

  const formatted = new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: decimals ?? (currency === "MNT" ? 0 : 2),
    maximumFractionDigits: decimals ?? (currency === "MNT" ? 0 : 2),
  }).format(amount);

  return currency === "MNT"
    ? `${formatted} ${config.symbol}`
    : `${config.symbol}${formatted}`;
};

/**
 * Товч харуулах
 * 1500000   →  "1.5M ₮"
 * 25000     →  "25K ₮"
 * 500       →  "500 ₮"
 */
const formatMoneyCompact = (amount, currency = "MNT") => {
  if (amount === null || amount === undefined) return "-";

  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.MNT;
  const symbol = config.symbol;
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  let result;
  if (abs >= 1_000_000_000) {
    result = `${sign}${(abs / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  } else if (abs >= 1_000_000) {
    result = `${sign}${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  } else if (abs >= 1_000) {
    result = `${sign}${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  } else {
    result = `${sign}${abs}`;
  }

  return currency === "MNT" ? `${result} ${symbol}` : `${symbol}${result}`;
};

/**
 * Хүү тооцоолох
 * calcInterest(1000000, 1.5, 12)  →  180000  (жилийн 1.5%, 12 сар)
 */
const calcInterest = (principal, monthlyRate, months) => {
  if (!principal || !monthlyRate || !months) return 0;
  return Math.round(principal * (monthlyRate / 100) * months);
};

/**
 * Сарын төлбөр тооцоолох (annuity)
 * calcMonthlyPayment(10000000, 1.5, 24)  →  сарын төлбөр
 */
const calcMonthlyPayment = (principal, monthlyRate, months) => {
  if (!principal || !monthlyRate || !months) return 0;
  const r = monthlyRate / 100;
  const payment =
    (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  return Math.round(payment);
};

// ═══════════════════════════════════════════════════
// 🔢 ТООН УТГА
// ═══════════════════════════════════════════════════

/**
 * Тоог форматлах — мянгын таслалтай
 * 1500000  →  "1,500,000"
 */
const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return new Intl.NumberFormat("mn-MN").format(num);
};

/**
 * Хувь харуулах
 * 0.156  →  "15.6%"
 * 1.5    →  "1.5%"
 */
const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return "-";
  return `${Number(value).toFixed(decimals)}%`;
};

module.exports = {
  // Огноо
  formatDate,
  formatDateTime,
  formatTime,
  formatDateFull,
  formatRelative,
  dateDiff,
  isToday,
  isPast,
  addTime,

  // Хугацаа
  formatDuration,
  formatMonths,
  formatDateRange,

  // Мөнгө
  formatMoney,
  formatMoneyCompact,
  calcInterest,
  calcMonthlyPayment,

  // Тоо
  formatNumber,
  formatPercent,
};

// // ── Огноо ──
// formatDate("2024-01-15")              // "2024.01.15"
// formatDateTime("2024-01-15T10:30:00") // "2024.01.15 10:30"
// formatRelative("2024-01-10")          // "21 өдрийн өмнө"

// // ── Хугацаа ──
// formatDuration(3600)   // "1 цаг"
// formatDuration(5400)   // "1 цаг 30 минут"
// formatDuration(90)     // "1 минут 30 секунд"
// formatMonths(18)       // "1 жил 6 сар"
// formatMonths(6)        // "6 сар"

// // ── Мөнгө ──
// formatMoney(1500000)                        // "1,500,000 ₮"
// formatMoney(1500000, "USD")                 // "$1,500,000.00"
// formatMoney(1500000, "MNT", { compact: true }) // "1.5M ₮"
// formatMoneyCompact(25000)                   // "25K ₮"

// // ── Тооцоолол ──
// calcMonthlyPayment(10_000_000, 1.5, 24)    // сарын төлбөр

// // ── Тоо / Хувь ──
// formatNumber(1500000)   // "1,500,000"
// formatPercent(1.5)      // "1.5%"
